import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Footer from '@/components/feature/Footer';

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password.trim()) {
      setError('이메일과 비밀번호를 모두 입력해주세요.');
      return;
    }

    setSubmitting(true);
    const { error: signInError } = await signIn(email.trim(), password);
    setSubmitting(false);

    if (signInError) {
      setError(signInError);
      return;
    }

    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <header className="bg-secondary-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">스</span>
              </div>
              <span className="text-xl font-bold text-primary-500">스카연</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg p-8 border border-foreground-200">
            <h1 className="text-2xl font-bold text-foreground-950 text-center mb-2">로그인</h1>
            <p className="text-sm text-foreground-600 text-center mb-8">
              스카연에 오신 것을 환영합니다
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    이메일
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1">
                    비밀번호
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호를 입력하세요"
                    className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    autoComplete="current-password"
                  />
                </div>

                {error && (
                  <div className="bg-primary-50 border border-primary-200 text-primary-700 text-sm px-4 py-3 rounded-md">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm py-3 rounded-md transition-colors whitespace-nowrap"
                >
                  {submitting ? '로그인 중...' : '로그인'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-foreground-600">
                아직 계정이 없으신가요?{' '}
                <Link to="/register" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">
                  회원가입
                </Link>
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-foreground-200">
              <Link
                to="/"
                className="block text-center text-sm text-foreground-600 hover:text-primary-500 transition-colors"
              >
                <i className="ri-arrow-left-line mr-1" />
                메인으로 돌아가기
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}